import React, { useState, useEffect, useCallback } from 'react';
import {
  EyeIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  UserCircleIcon,
  ChartBarIcon,
  TagIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import {
  subscribeToAttendanceRecords,
  exportAttendanceToExcel,
  getLocalDateString,
} from '../../services/firebaseService';
import { ClassAttendanceRecord, ClassSettings } from '../../types';

interface WatchingStatsPanelProps {
  classSettings: ClassSettings | null;
  isAdmin?: boolean;
}

const ACTIVE_THRESHOLD_SECONDS = 90;

const fmt = (secs: number) => {
  const m = Math.floor(secs / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m`;
};

const formatTime = (ts: any): string => {
  if (!ts) return '—';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
};

const WatchingStatsPanel: React.FC<WatchingStatsPanelProps> = ({
  classSettings,
  isAdmin = false,
}) => {
  const [records, setRecords] = useState<ClassAttendanceRecord[]>([]);
  const [filterLabel, setFilterLabel] = useState<string>('__all__');
  const [filterDate, setFilterDate] = useState<string>(
    getLocalDateString()
  );
  const [activeTab, setActiveTab] = useState<'live' | 'all' | 'classes'>('live');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [nowSec, setNowSec] = useState<number>(Math.floor(Date.now() / 1000));

  // Ticker to refresh active/idle status every 10s even without Firestore events
  useEffect(() => {
    const timer = setInterval(() => {
      setNowSec(Math.floor(Date.now() / 1000));
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const unsub = subscribeToAttendanceRecords((newRecords) => {
      setRecords(newRecords);
      setLastUpdated(new Date());
    }, filterDate);
    return () => unsub();
  }, [filterDate]);

  const isActivelyWatching = useCallback(
    (r: ClassAttendanceRecord) => {
      const lastSeenSec = r.lastSeenAt?.seconds || 0;
      return nowSec - lastSeenSec <= ACTIVE_THRESHOLD_SECONDS;
    },
    [nowSec]
  );

  const todayRecords = records.filter((r) => r.sessionDate === filterDate);
  const liveRecords = todayRecords.filter(isActivelyWatching);
  const totalWatching = liveRecords.length;

  const classLabels = Array.from(
    new Set(todayRecords.map((r) => r.liveLabel || 'General Session').filter(Boolean))
  );

  const filteredRecords =
    filterLabel === '__all__'
      ? todayRecords
      : todayRecords.filter((r) => (r.liveLabel || 'General Session') === filterLabel);

  const classSummary = classLabels.map((label) => {
    const inClass = todayRecords.filter(
      (r) => (r.liveLabel || 'General Session') === label
    );
    const liveInClass = inClass.filter(isActivelyWatching);
    const totalMinutes = Math.round(
      inClass.reduce((sum, r) => sum + (r.totalWatchSeconds || 0), 0) / 60
    );
    return { label, total: inClass.length, live: liveInClass.length, totalMinutes };
  });

  const handleExport = () => {
    if (filteredRecords.length === 0) {
      alert('No attendance records to export for the selected filter.');
      return;
    }
    exportAttendanceToExcel(
      filteredRecords,
      `Attendance_${filterDate}${filterLabel !== '__all__' ? `_${filterLabel.slice(0, 30)}` : ''}.xlsx`
    );
  };

  return (
    <div className="space-y-4">
      {/* Top Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="col-span-2 sm:col-span-1 bg-gradient-to-br from-emerald-900/60 to-teal-900/40 border border-emerald-500/30 rounded-2xl p-4 flex items-center space-x-3 shadow-lg">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
            <EyeIcon className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <p className="text-2xl font-black text-white tabular-nums leading-none">{totalWatching}</p>
            <p className="text-[11px] text-emerald-300 font-semibold mt-0.5">Live Watching</p>
          </div>
          <span className="ml-auto">
            <span className="flex h-2.5 w-2.5 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
          </span>
        </div>

        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center flex-shrink-0">
            <UserCircleIcon className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <p className="text-2xl font-black text-white tabular-nums leading-none">{todayRecords.length}</p>
            <p className="text-[11px] text-slate-400 font-semibold mt-0.5">Unique Viewers</p>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center flex-shrink-0">
            <ClockIcon className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <p className="text-2xl font-black text-white tabular-nums leading-none">
              {Math.round(todayRecords.reduce((s, r) => s + (r.totalWatchSeconds || 0), 0) / 60)}
            </p>
            <p className="text-[11px] text-slate-400 font-semibold mt-0.5">Total Watch-Mins</p>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
            <ChartBarIcon className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <p className="text-2xl font-black text-white tabular-nums leading-none">{classSummary.length}</p>
            <p className="text-[11px] text-slate-400 font-semibold mt-0.5">Session Groups</p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center space-x-1 bg-slate-900/60 border border-slate-800 rounded-xl p-1">
          {(['live', 'all', 'classes'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                activeTab === tab
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {tab === 'live' && `🟢 Live Now (${totalWatching})`}
              {tab === 'all' && `👥 All Today (${todayRecords.length})`}
              {tab === 'classes' && `📊 By Class (${classSummary.length})`}
            </button>
          ))}
        </div>

        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1.5 bg-slate-900/60 border border-slate-800 rounded-xl px-2.5 py-1.5">
            <FunnelIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="bg-transparent text-white text-xs focus:outline-none w-28"
            />
          </div>

          {activeTab !== 'classes' && classLabels.length > 1 && (
            <div className="flex items-center space-x-1.5 bg-slate-900/60 border border-slate-800 rounded-xl px-2.5 py-1.5">
              <TagIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <select
                value={filterLabel}
                onChange={(e) => setFilterLabel(e.target.value)}
                className="bg-transparent text-white text-xs focus:outline-none"
              >
                <option value="__all__">All Classes</option>
                {classLabels.map((l) => (
                  <option key={l} value={l}>{l.length > 30 ? l.slice(0, 30) + '…' : l}</option>
                ))}
              </select>
            </div>
          )}

          <button
            type="button"
            onClick={handleExport}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold transition-colors shadow-md shadow-indigo-600/20"
          >
            <ArrowDownTrayIcon className="w-3.5 h-3.5" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Last updated line */}
      <div className="flex items-center space-x-1.5 text-[11px] text-slate-500">
        <ArrowPathIcon className="w-3 h-3 animate-spin" style={{ animationDuration: '3s' }} />
        <span>Live — last updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
      </div>

      {activeTab === 'live' && (
        <WatcherTable records={liveRecords} isActivelyWatching={isActivelyWatching} emptyMessage="No students are actively watching right now." showLiveDot />
      )}
      {activeTab === 'all' && (
        <WatcherTable records={filteredRecords} isActivelyWatching={isActivelyWatching} emptyMessage={`No attendance records found for ${filterDate}.`} />
      )}
      {activeTab === 'classes' && (
        <div className="space-y-4">
          {classSummary.length === 0 ? (
            <div className="p-8 text-center bg-slate-950/40 rounded-xl border border-slate-800">
              <ChartBarIcon className="w-10 h-10 text-slate-700 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No class sessions found for {filterDate}.</p>
            </div>
          ) : (
            classSummary.map((cls) => (
              <div key={cls.label} className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center space-x-2 min-w-0">
                    <TagIcon className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                    <h4 className="font-bold text-sm text-white truncate">{cls.label}</h4>
                  </div>
                  <div className="flex items-center space-x-3 text-xs flex-shrink-0">
                    <span className="flex items-center space-x-1 text-emerald-400 font-bold">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      <span>{cls.live} live</span>
                    </span>
                    <span className="text-slate-400">{cls.total} total</span>
                    <span className="text-purple-400">{cls.totalMinutes} min watched</span>
                  </div>
                </div>
                <WatcherTable
                  records={todayRecords.filter((r) => (r.liveLabel || 'General Session') === cls.label)}
                  isActivelyWatching={isActivelyWatching}
                  emptyMessage="No viewers."
                  compact
                />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ─── Internal Table ────────────────────────────────────────────────────────────

interface WatcherTableProps {
  records: ClassAttendanceRecord[];
  isActivelyWatching: (r: ClassAttendanceRecord) => boolean;
  emptyMessage: string;
  showLiveDot?: boolean;
  compact?: boolean;
}

const WatcherTable: React.FC<WatcherTableProps> = ({
  records,
  isActivelyWatching,
  emptyMessage,
  showLiveDot = false,
  compact = false,
}) => {
  if (records.length === 0) {
    return (
      <div className={`text-center ${compact ? 'py-4' : 'py-8'} bg-slate-950/40 rounded-xl border border-slate-800`}>
        <p className="text-sm text-slate-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-950/40 border border-slate-800 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/60">
              <th className="px-3 py-2.5 text-left text-slate-400 font-semibold">Student</th>
              <th className="px-3 py-2.5 text-left text-slate-400 font-semibold hidden sm:table-cell">Reg ID</th>
              <th className="px-3 py-2.5 text-left text-slate-400 font-semibold">Watch Time</th>
              <th className="px-3 py-2.5 text-left text-slate-400 font-semibold hidden md:table-cell">First Joined</th>
              <th className="px-3 py-2.5 text-left text-slate-400 font-semibold hidden md:table-cell">Last Seen</th>
              <th className="px-3 py-2.5 text-center text-slate-400 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {records.map((r) => {
              const active = isActivelyWatching(r);
              return (
                <tr key={r.id || `${r.registrationNumber}_${r.sessionDate}`} className={`transition-colors ${active ? 'bg-emerald-950/10 hover:bg-emerald-950/20' : 'hover:bg-slate-900/40'}`}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center space-x-2">
                      {showLiveDot && (
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${active ? 'bg-emerald-500 animate-pulse' : 'bg-slate-600'}`} />
                      )}
                      <span className="font-medium text-white truncate max-w-[120px]">{r.studentName}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 hidden sm:table-cell">
                    <span className="font-mono text-slate-300">{r.regNoSuffix}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`font-semibold ${active ? 'text-emerald-400' : 'text-slate-300'}`}>
                      {fmt(r.totalWatchSeconds || 0)}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 hidden md:table-cell text-slate-400">{formatTime(r.firstJoinedAt)}</td>
                  <td className="px-3 py-2.5 hidden md:table-cell text-slate-400">{formatTime(r.lastSeenAt)}</td>
                  <td className="px-3 py-2.5 text-center">
                    {active ? (
                      <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 font-semibold text-[10px]">
                        <CheckCircleIcon className="w-3 h-3" />
                        <span>Live</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-slate-800 text-slate-500 border border-slate-700 font-semibold text-[10px]">
                        <XCircleIcon className="w-3 h-3" />
                        <span>Away</span>
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 border-t border-slate-800 text-[11px] text-slate-500">
        {records.length} record{records.length !== 1 ? 's' : ''} • Watch time updates every 30s via heartbeat
      </div>
    </div>
  );
};

export default WatchingStatsPanel;
